import React, { useState, useEffect } from "react";
import { Button, Modal, Form, Alert, Row, Col, Card, Spinner, InputGroup, Badge } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import PermissionWrapper from "../components/PermissionWrapper";
import { getAllUsers, createUserByAdmin, updateUser, deleteUser } from "../services/UserService";
import UserData from "../models/UserData";
import { IMAGE_BASE } from "../Config";
import { apiGet, apiPost } from "../services/BaseService";

const ManageAllUsersPage: React.FC = () => {
  const [users, setUsers] = useState<UserData[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserData | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalUsers, setTotalUsers] = useState(0);
  const [formData, setFormData] = useState<UserData>({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
    phone: "",
    address: "",
    aboutUser: "",
    profileImage: "",
    userRoleCode: "ROLE_GENERAL_USER"
  });
  const [userRoles, setUserRoles] = useState<any[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchUsers(true);
    fetchUserRoles();
  }, []);

  const fetchUserRoles = async () => {
    try {
      const roles = await apiGet('/userRoles');
      setUserRoles(roles);
    } catch (error) {
      setError("Failed to fetch user roles");
    }
  };

  const fetchUsers = async (reset: boolean = false, searchTerm: string = '', roleTerm: string = '') => {
    try {
      if (reset) {
        setLoading(true);
        setPage(1);
      } else {
        setLoadingMore(true);
      }
      
      const currentPage = reset ? 1 : page;
      const response = await getAllUsers(currentPage, 12, searchTerm, roleTerm);
      
      if (reset) {
        setUsers(response.users);
      } else {
        setUsers(prev => [...prev, ...response.users]);
      }
      
      setHasMore(response.hasMore);
      setTotalUsers(response.totalUsers);
      setPage(currentPage + 1);
    } catch (error) {
      setError("Failed to fetch users");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleSearch = (query: string) => {
    if( query.trim().length < 3 && query.trim() !== "") {
      setError("Search query must be at least 3 characters long");
      setSearchQuery(query);
    }
    else if (query.trim() === searchQuery) {
      setError("You are already searching for this term");
      setSearchQuery(query);
    }
    else{
    setError(null);
    setSearchQuery(query);
    fetchUsers(true, query, roleFilter);
    }
  };

  const handleRoleFilter = (role: string) => {
    setRoleFilter(role);
    fetchUsers(true, searchQuery, role);
  };

  const handleSort = () => {
    const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    setSortOrder(newOrder);
    
    const sortedUsers = [...users].sort((a, b) => {
      const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.username;
      const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim() || b.username;
      
      if (newOrder === 'asc') {
        return nameA.localeCompare(nameB);
      } else {
        return nameB.localeCompare(nameA);
      }
    });
    
    setUsers(sortedUsers);
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchUsers(false, searchQuery, roleFilter);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch(searchQuery);
    }
  };

  const getRoleBadgeColor = (roleCode: string) => {
    switch (roleCode) {
      case 'ROLE_SUPER_ADMIN': return 'danger';
      case 'ROLE_ADMIN': return 'warning';
      case 'ROLE_BUSINESS_USER': return 'info';
      case 'ROLE_GENERAL_USER': return 'secondary';
      default: return 'secondary';
    }
  };

  const getRoleDisplayName = (roleName: string) => {
    if (roleName?.includes('ROLE_')) {
      return roleName.replace('ROLE_', '').replace('_', ' ');
    }
    return roleName || 'General User';
  };

  const handleShowModal = (user?: UserData) => {
    if (user) {
      setEditingUser(user);
      setFormData({ 
        ...user, 
        password: "",
        confirmPassword: ""
      });
      setShowPasswordSection(false);
    } else {
      setEditingUser(null);
      setFormData({
        username: "",
        email: "",
        password: "",
        confirmPassword: "",
        firstName: "",
        lastName: "",
        phone: "",
        address: "",
        aboutUser: "",
        profileImage: "",
        userRoleCode: "ROLE_GENERAL_USER"
      });
      setShowPasswordSection(true);
    }
    setSelectedFile(null);
    setPasswordError("");
    setShowModal(true);
    setError(null);
    setSuccess(null);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setError(null);
    setSuccess(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === 'password' || name === 'confirmPassword') {
      setPasswordError("");
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const uploadImage = async (): Promise<string | null> => {
    if (!selectedFile) return null;
    
    try {
      setUploading(true);
      const uploadFormData = new FormData();
      uploadFormData.append('file', selectedFile);
      
      const result = await apiPost('/image/upload/profileImage', uploadFormData);
      return `/profileImage/${result.fileName}`;
    } catch (error) {
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const validatePassword = () => {
    if (showPasswordSection && formData.password) {
      if (formData.password.length < 6) {
        setPasswordError("Password must be at least 6 characters long");
        return false;
      }
      if (formData.password !== formData.confirmPassword) {
        setPasswordError("Passwords do not match");
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validatePassword()) {
      return;
    }
    
    try {
      let imageFileName = formData.profileImage;
      
      if (selectedFile) {
        imageFileName = (await uploadImage()) || '';
      }
      
      const submitData = { 
        ...formData, 
        profileImage: imageFileName || '',
        ...((!showPasswordSection || !formData.password) && { password: undefined })
      };
      delete submitData.confirmPassword;
      
      if (editingUser) {
        await updateUser(editingUser._id, submitData);
        setSuccess("User updated successfully");
      } else {
        await createUserByAdmin(submitData);
        setSuccess("User created successfully");
      }
      await fetchUsers(true, searchQuery, roleFilter);
      handleCloseModal();
    } catch (error) {
      setError(editingUser ? "Failed to update user" : "Failed to create user");
    }
  };

  const handleDelete = async (userId: string) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        await deleteUser(userId);
        setSuccess("User deleted successfully");
        await fetchUsers(true, searchQuery, roleFilter);
      } catch (error) {
        setError("Failed to delete user");
      }
    }
  };

  return (
    <PermissionWrapper pageName="ManageAllUsersPage">
      <div>
        <Row className="mb-4">
          <Col>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
              <h3 className="mb-3 mb-md-0">
                <i className="bi bi-people me-2"></i>
                Manage All Users
              </h3>
              <Button variant="primary" onClick={() => handleShowModal()}>
                <i className="bi bi-plus-circle me-2"></i>
                Add User
              </Button>
            </div>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={4}>
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="Search users by name, email, or phone..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <Button variant="outline-secondary" onClick={() => handleSearch(searchQuery)}>
                <i className="bi bi-search"></i>
              </Button>
            </InputGroup>
          </Col>
          <Col md={3}>
            <Form.Select
              value={roleFilter}
              onChange={(e) => handleRoleFilter(e.target.value)}
            >
              <option value="">All Roles</option>
              {userRoles.map(role => (
                <option key={role.userRoleCode} value={role.userRoleCode}>
                  {role.userRoleName}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={2}>
            <Button 
              variant="outline-secondary" 
              onClick={handleSort}
              className="w-100"
            >
              <i className={`bi bi-sort-alpha-${sortOrder === 'asc' ? 'down' : 'up'} me-1`}></i>
              A-Z
            </Button>
          </Col>
          <Col md={3} className="d-flex align-items-center">
            <small className="text-muted">
              {users.length > 0 ? `Showing ${users.length} of ${totalUsers} users` : "No users found"}
              {searchQuery && ` for "${searchQuery}"`}
              {roleFilter && ` with role "${userRoles.find(r => r.userRoleCode === roleFilter)?.userRoleName}"`}
            </small>
          </Col>
        </Row>

        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : (
          <Row className="g-3">
            {users.map((user) => (
              <Col xs={12} sm={6} md={4} lg={3} key={user._id}>
                <Card className="h-100 user-card">
                  <div className="user-image-container">
                    {user.profileImage ? (
                      <Card.Img 
                        variant="top" 
                        src={IMAGE_BASE + user.profileImage}
                        className="user-image"
                        // onError={(e) => {
                        //   (e.target as HTMLImageElement).src = '/default-profile-image.jpg';
                        // }}
                      />
                    ) : (
                      <div className="user-image-placeholder">
                        <i className="bi bi-person-circle display-4 text-muted"></i>
                      </div>
                    )}
                    <div className="position-absolute top-0 end-0 m-2">
                      <Badge bg={getRoleBadgeColor(user.userRoleCode||'')} className="user-role-badge">
                        {getRoleDisplayName(user.userRoleName || user.userRoleCode || '--')}
                      </Badge>
                    </div>
                  </div>
                  <Card.Body className="d-flex flex-column">
                    <Card.Title className="h6">
                      {`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username}
                    </Card.Title>
                    <Card.Text className="small text-muted flex-grow-1">
                      {user.aboutUser ? 
                        (user.aboutUser.length > 80 ? 
                          user.aboutUser.substring(0, 80) + '...' : 
                          user.aboutUser
                        ) : 'No description available'
                      }
                    </Card.Text>
                    <div className="user-info mb-2">
                      <div className="small">
                        <i className="bi bi-person me-1"></i>
                        {user.username}
                      </div>
                      {user.email && (
                        <div className="small">
                          <i className="bi bi-envelope me-1"></i>
                          {user.email}
                        </div>
                      )}
                      {user.phone && (
                        <div className="small">
                          <i className="bi bi-telephone me-1"></i>
                          {user.phone}
                        </div>
                      )}
                      {user.address && (
                        <div className="small text-muted">
                          <i className="bi bi-geo-alt me-1"></i>
                          {user.address.length > 30 ? user.address.substring(0, 30) + '...' : user.address}
                        </div>
                      )}
                    </div>
                    <div className="d-flex gap-1 mt-auto">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleShowModal(user)}
                        className="flex-fill"
                      >
                        <i className="bi bi-pencil me-1"></i>
                        Edit
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(user._id!)}
                      >
                        <i className="bi bi-trash"></i>
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        )}
        {hasMore && (
          <Row className="mt-4">
            <Col xs={12} className="text-center">
              <Button 
                variant="outline-primary" 
                size="lg" 
                onClick={handleLoadMore} 
                disabled={loadingMore}
              >
                {loadingMore ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Loading...
                  </>
                ) : (
                  "Load More Users"
                )}
              </Button>
            </Col>
          </Row>
        )}
  
        <Modal show={showModal} onHide={handleCloseModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{editingUser ? "Edit User" : "Add User"}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Username</Form.Label>
                    <Form.Control
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>First Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="firstName"
                      value={formData.firstName || ""}
                      onChange={handleInputChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Last Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="lastName"
                      value={formData.lastName || ""}
                      onChange={handleInputChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Form.Group className="mb-3">
                <Form.Label>Phone</Form.Label>
                <Form.Control
                  type="tel"
                  name="phone"
                  value={formData.phone || ""}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Profile Image</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                />
                <Form.Text className="text-muted">
                  Upload a profile image (JPG, PNG, GIF, SVG)
                </Form.Text>
                {formData.profileImage && (
                  <div className="mt-2">
                    <img 
                      src={IMAGE_BASE + formData.profileImage}
                      alt="Current profile image"
                      style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                      className="rounded"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = '/default-profile-image.jpg';
                      }}
                    />
                  </div>
                )}
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>User Role</Form.Label>
                <Form.Select
                  name="userRoleCode"
                  value={formData.userRoleCode || "ROLE_GENERAL_USER"}
                  onChange={handleInputChange}
                  required
                >
                  {userRoles.map(role => (
                    <option key={role.userRoleCode} value={role.userRoleCode}>
                      {role.userRoleName}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              
              {/* Password Section */}
              {editingUser ? (
                <div className="mb-3">
                  <Button 
                    variant="outline-secondary" 
                    size="sm"
                    onClick={() => setShowPasswordSection(!showPasswordSection)}
                    type="button"
                  >
                    <i className="bi bi-key me-2"></i>
                    {showPasswordSection ? 'Cancel Password Update' : 'Update Password'}
                  </Button>
                </div>
              ) : null}
              
              {showPasswordSection && (
                <>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Password</Form.Label>
                        <Form.Control
                          type="password"
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          required={!editingUser}
                          minLength={6}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Confirm Password</Form.Label>
                        <Form.Control
                          type="password"
                          name="confirmPassword"
                          value={formData.confirmPassword || ""}
                          onChange={handleInputChange}
                          required={!editingUser}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  {passwordError && (
                    <Alert variant="danger" className="mb-3">
                      {passwordError}
                    </Alert>
                  )}
                </>
              )}
              
              <Form.Group className="mb-3">
                <Form.Label>About User</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="aboutUser"
                  value={formData.aboutUser || ""}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={uploading}>
                {uploading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Uploading...
                  </>
                ) : (
                  <>{editingUser ? "Update" : "Create"} User</>
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </div>
    </PermissionWrapper>
  );
};

export default ManageAllUsersPage;
